import { round, mean } from 'lodash';
import fetchWithOptions from '../../../shared/utils/fetch';
import { QueryParam, URLParams } from '../../../shared/utils/urlParams';
import {
  Delineation,
  ServerDelineation,
} from '../../delineation/types/delineation.type';
import {
  Diagnostic,
  ServerDiagnostic,
} from '../../diagnostic/types/diagnostic';
import { ANNOTATION_TABLE_DEFAULT } from '../../diagnostic/utils/diagnostic.factory';
import { Metric } from '../../metric/types/metric.type';
import { RawSignalState } from '../../raw-signal/reducers/raw-signal.reducer';
import { secondsToMs } from '../../utils/time';
import {
  Ecg,
  EcgItem,
  HolterStripPayload,
  ParentHolter,
  PatientInfoType,
  ServerEcg,
  ServerEcgItem,
  ServerFile,
  ServerHolterStripInfo,
  SexWordToLetterDict,
} from '../types/ecg.type';
import { QrsOnset, ServerQrsOnset } from '../types/qrs-onset.type';
import { Leads, Means, Signal } from '../types/signal';

export const getEcg = async (ecgId: string): Promise<Ecg | null> => {
  try {
    const serverEcg = await fetchWithOptions<ServerEcg>(`/api/ecg/${ecgId}`);

    const ecg: Ecg = {
      id: ecgId,
      analysisType: serverEcg.analysis_type,
      recordType: serverEcg.record_type,
      dashboardMeasurements: jsonToMetric(serverEcg.dashboard_measurements),
      delineations: jsonToDelineations(serverEcg.delineations),
      diagnostics: jsonToDiagnostics(serverEcg.diagnostics),
      duration: jsonToDuration(serverEcg.duration),
      stripInfo: jsonToStripInfo(serverEcg.holter_strip_info),
      patientInfo: jsonToPatientInfo(serverEcg.patient_info),
    };

    handleErrorType(ecg, serverEcg);
    return ecg;
  } catch (e) {
    return null;
  }
};

const handleErrorType = (ecg: Ecg, serverEcg: ServerEcg) => {
  if (serverEcg.error_type != null) {
    ecg.errorType = serverEcg.error_type;
  }
};

const jsonToDelineations = (
  serverDelineations: ServerDelineation[],
): Delineation[] => {
  const allDelineations: Delineation[] = [];
  const frozenDelineationMap: {
    [expertName: string]: Delineation[];
  } = {};
  // delineations that have a pipeline resting are coming from holters and have no
  // fiducial points so they should be removed
  const parsedDelineations = serverDelineations
    .map(jsonToDelineation)
    .filter((delineation) =>
      ['resting', 'mct', 'rpm'].some(
        (pipeline) => pipeline === delineation.pipeline,
      ),
    );

  parsedDelineations.forEach((delineation) => {
    if (delineation.immutable === false) {
      allDelineations.push(delineation);
    } else {
      const frozenDelineations =
        frozenDelineationMap[delineation.expertName] ?? [];
      frozenDelineations.push(delineation);
      frozenDelineationMap[delineation.expertName] = frozenDelineations;
    }
  });

  for (const expertName in frozenDelineationMap) {
    const first = frozenDelineationMap[expertName].sort(
      sortDelineationsByLatest,
    )[0];
    allDelineations.push(first);
  }

  return allDelineations.sort(sortDelineationsByLatest);
};

const jsonToDelineation = ({
  id,
  created_at,
  updated_at,
  expert_name,
  expert_role,
  pipeline,
  immutable,
}: ServerDelineation): Delineation => {
  return {
    id,
    createdAt: created_at,
    updatedAt: updated_at,
    expertName: expert_name ?? '',
    expertRole: expert_role ?? 'algo',
    immutable: immutable ?? false,
    pipeline: pipeline,
  };
};

const jsonToDiagnostics = (
  serverDiagnostics: ServerDiagnostic[],
): Diagnostic[] => {
  const allDiagnostics: Diagnostic[] = [];
  const frozenDiagnosticMap: {
    [annotationTable: string]: Diagnostic[];
  } = {};
  const parsedDiagnostics = serverDiagnostics.map(jsonToDiagnostic);

  parsedDiagnostics.forEach((diagnostic) => {
    // if the diagnostic does not belong to an annotation table the field annotationTable will take
    // the default value '--'

    if (diagnostic.pipeline !== 'immutable') {
      allDiagnostics.push(diagnostic);
    } else {
      if (diagnostic.annotationTable !== ANNOTATION_TABLE_DEFAULT) {
        const frozenDiagnostics =
          frozenDiagnosticMap[diagnostic.annotationTable] ?? [];
        frozenDiagnostics.push(diagnostic);
        frozenDiagnosticMap[diagnostic.annotationTable] = frozenDiagnostics;
      }
    }
  });

  for (const annotationTable in frozenDiagnosticMap) {
    const first = frozenDiagnosticMap[annotationTable].sort(
      sortDiagnosticByLatest,
    )[0];
    allDiagnostics.push(first);
  }

  return allDiagnostics.sort(sortDiagnosticByLatest);
};

const jsonToDiagnostic = ({
  abnormalities,
  comment,
  expert_name,
  expert_role,
  expert_uuid,
  id,
  updated_at,
  pipeline,
  coding_table,
  report_parameters,
}: ServerDiagnostic): Diagnostic => {
  return {
    annotationTable: coding_table ?? ANNOTATION_TABLE_DEFAULT,
    abnormalities,
    comment: comment ?? '',
    expertName: expert_name,
    expertRole: expert_role,
    expertId: expert_uuid,
    id,
    updatedAt: updated_at,
    pipeline,
    suggestionsLogs:
      report_parameters?.annotator_comment?.autoLabelsLogs ?? null,
  };
};

const jsonToDuration = (duration: number | null): number => {
  if (duration == null) {
    return 0;
  }

  return secondsToMs(duration, false);
};

const parentJsonToDuration = (duration: number | null): number => {
  if (duration == null) {
    return 0;
  }

  return secondsToMs(duration);
};

const jsonToMetric = (dashboardMeasurements: Metric[] | null): Metric[] => {
  if (dashboardMeasurements == null) {
    return [];
  }
  return dashboardMeasurements.map(({ description, unit, value }) => ({
    description,
    unit,
    value,
  }));
};

const jsonToStripInfo = (
  stripInfo: ServerHolterStripInfo | undefined,
): HolterStripPayload | undefined => {
  if (stripInfo == null) {
    return undefined;
  }

  return {
    parentId: stripInfo.parent_uuid,
    start: secondsToMs(stripInfo.start_time),
  };
};

const jsonToPatientInfo = (patientInfo: PatientInfoType | undefined): any => {
  if (patientInfo == null) {
    return undefined;
  }

  return {
    age: patientInfo.age,
    sex: patientInfo.sex ? SexWordToLetterDict[patientInfo.sex] : null,
  };
};

export const getSignal = async (ecgId: string): Promise<Signal | null> => {
  try {
    const signal = await fetchWithOptions<ServerFile>(`/api/ecg/${ecgId}/file`);

    return jsonToEcgSignal(signal);
  } catch {
    return null;
  }
};

const jsonToEcgSignal = ({
  data = {},
  frequency = 1,
  mV_per_unit: mvPerUnit = 1,
}: ServerFile): Signal => {
  const leads: Leads = {};

  Object.keys(data).reduce((leadsAcc, leadName) => {
    leadsAcc[leadName] = data[leadName].map((amplitude) =>
      round(amplitude * mvPerUnit, 6),
    );

    return leadsAcc;
  }, leads);

  return {
    frequency,
    leads,
  };
};

const jsonToEcgRawSignal = ({
  data = {},
  frequency = 1,
  mV_per_unit: mvPerUnit = 1,
}: ServerFile): RawSignalState => {
  const leads: Leads = {};

  Object.keys(data).reduce((leadsAcc, leadName) => {
    leadsAcc[leadName] = data[leadName].map((amplitude) =>
      round(amplitude * mvPerUnit, 6),
    );

    return leadsAcc;
  }, leads);

  const means: Means = {};
  Object.keys(leads).forEach((leadName) => {
    means[leadName] = mean(leads[leadName]);
  });

  return {
    frequency,
    leads,
    means,
  };
};

export const getRawSignal = async (
  ecgId: string,
): Promise<RawSignalState | null> => {
  try {
    const rawSignal = await fetchWithOptions<ServerFile>(
      `/api/ecg/${ecgId}/raw`,
    );
    return jsonToEcgRawSignal(rawSignal);
  } catch {
    return null;
  }
};

export const getQrsOnsets = async (
  ecgId: string,
  hour: number,
): Promise<QrsOnset[] | null> => {
  try {
    const response = await fetchWithOptions<ServerQrsOnset[]>(
      `/api/ecg/${ecgId}/qrs_onsets/${hour}`,
    );
    return response.map(jsonToQrsOnsets);
  } catch {
    // the request will fail if the parent ecg is not in the user's datasets
    return null;
  }
};

const jsonToQrsOnsets = (qrsOnset: ServerQrsOnset): QrsOnset => {
  return {
    time: secondsToMs(parseFloat(qrsOnset.time)) || 0,
  };
};

export const reprocessEcg = async (ecgId: string): Promise<boolean> => {
  try {
    const response = await fetchWithOptions(`/api/ecg/${ecgId}/reprocess`, {
      method: 'POST',
      body: JSON.stringify({ pipeline: 'resting', reset_status: false }),
    });

    return !!response;
  } catch {
    return false;
  }
};

export const ECG_ITEMS_PER_PAGE = 500;

export const getEcgList = async (
  customParams: QueryParam = {},
): Promise<EcgItem[] | null> => {
  try {
    const queryParamString = new URLParams({
      record_type: ['resting', 'event', 'rpm', 'mct'],
      order_by_time: 'asc',
      show_status: 0,
      page_size: ECG_ITEMS_PER_PAGE,
      status: ['success'],
      ...customParams,
    });

    const response = await fetchWithOptions<ServerEcgItem[]>(
      `/api/ecg/search?${queryParamString}`,
    );

    return jsonToEcgList(response);
  } catch {
    return null;
  }
};

const jsonToEcgList = (json: ServerEcgItem[]): EcgItem[] => {
  return json.map(({ uuid }) => ({ id: uuid }));
};

const sortDiagnosticByLatest = (a: Diagnostic, b: Diagnostic): number => {
  return b.updatedAt.localeCompare(a.updatedAt);
};

const sortDelineationsByLatest = (a: Delineation, b: Delineation): number => {
  return b.updatedAt.localeCompare(a.updatedAt);
};

export const getParentHolter = async (
  parentId: string,
): Promise<ParentHolter | null> => {
  try {
    const ecg = await fetchWithOptions<ParentHolter>(`/api/ecg/${parentId}`);

    return {
      duration: parentJsonToDuration(ecg.duration),
    };
  } catch (e) {
    return null;
  }
};

interface ResetResponse {
  code: number;
  message: string;
}
export const resetEcg = async (ecgId: string): Promise<boolean> => {
  try {
    const response = await fetchWithOptions<ResetResponse>(
      `/api/ecg/${ecgId}/episodes/reset`,
      {
        method: 'DELETE',
      },
    );

    if (response.message === 'success') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};
